import { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

export default function TiersManager() {
  const [tiers, setTiers] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [t, r] = await Promise.all([
      api.get("/tiers"),
      api.get("/rewards/rules"),
    ]);
    setTiers(t.data || []);
    setRules(r.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateTier = (id, key, value) => {
    setTiers((prev) => prev.map((t) => (t.id === id ? { ...t, [key]: value } : t)));
  };

  const saveTier = async (t) => {
    try {
      await api.put(`/tiers/${t.id}`, {
        slug: t.slug, name: t.name, price: Number(t.price) || 0, interval: t.interval || "month",
        level: Number(t.level) || 0, color: t.color || "#D4AF37", badge_emoji: t.badge_emoji || "",
        points_threshold: Number(t.points_threshold) || 0, benefits: t.benefits || [],
        stripe_price_id: t.stripe_price_id || "", is_visible: t.is_visible !== false,
      });
      toast.success(`${t.name} saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const addTier = async () => {
    const slug = prompt("New tier slug (e.g. platinum):");
    if (!slug) return;
    try {
      const { data } = await api.post("/tiers", {
        slug, name: slug.charAt(0).toUpperCase() + slug.slice(1),
        price: 0, interval: "month", level: 4, color: "#D4AF37",
        badge_emoji: "◆", points_threshold: 0, benefits: [], is_visible: true,
      });
      setTiers((p) => [...p, data]);
      toast.success("Tier created");
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const deleteTier = async (id, name) => {
    if (!window.confirm(`Delete tier "${name}"?`)) return;
    await api.delete(`/tiers/${id}`);
    setTiers((p) => p.filter((t) => t.id !== id));
    toast.success("Deleted");
  };

  const saveRule = async (r) => {
    await api.put(`/rewards/rules/${r.id}`, { event: r.event, points: Number(r.points) || 0, note: r.note || "" });
    toast.success("Rule updated");
  };

  if (loading) return <div className="text-white/40">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="tiers-manager">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl text-white tracking-wider">Membership Tiers</h2>
          <p className="text-white/40 text-xs mt-1">Edit pricing, benefits, and gamification thresholds.</p>
        </div>
        <button onClick={addTier} data-testid="add-tier-btn" className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Add Tier
        </button>
      </div>

      <div className="grid gap-4">
        {tiers.map((t) => (
          <div key={t.id} className="bg-[#0A0A0A] border border-white/5 rounded p-5" data-testid={`tier-row-${t.slug}`}>
            <div className="grid md:grid-cols-6 gap-3 mb-4">
              <input className="input-dark" value={t.name} placeholder="Name" onChange={(e) => updateTier(t.id, "name", e.target.value)} data-testid={`tier-name-${t.slug}`} />
              <input className="input-dark" value={t.slug} placeholder="slug" onChange={(e) => updateTier(t.id, "slug", e.target.value)} />
              <input className="input-dark" type="number" step="0.01" value={t.price} placeholder="Price" onChange={(e) => updateTier(t.id, "price", e.target.value)} data-testid={`tier-price-${t.slug}`} />
              <select className="input-dark" value={t.interval || "month"} onChange={(e) => updateTier(t.id, "interval", e.target.value)}>
                <option value="month">Monthly</option><option value="year">Yearly</option><option value="lifetime">One-time</option>
              </select>
              <input className="input-dark" type="number" value={t.level} placeholder="Level" onChange={(e) => updateTier(t.id, "level", e.target.value)} />
              <input className="input-dark" type="number" value={t.points_threshold} placeholder="Points required" onChange={(e) => updateTier(t.id, "points_threshold", e.target.value)} />
            </div>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <input className="input-dark" value={t.badge_emoji} placeholder="Badge (◆ ◇ ★ ▲)" onChange={(e) => updateTier(t.id, "badge_emoji", e.target.value)} />
              <input className="input-dark" value={t.color} placeholder="#D4AF37" onChange={(e) => updateTier(t.id, "color", e.target.value)} />
              <input className="input-dark" value={t.stripe_price_id || ""} placeholder="Stripe Price ID (optional)" onChange={(e) => updateTier(t.id, "stripe_price_id", e.target.value)} />
            </div>
            <textarea
              className="input-dark w-full"
              rows={3}
              placeholder="Benefits (one per line)"
              value={(t.benefits || []).join("\n")}
              onChange={(e) => updateTier(t.id, "benefits", e.target.value.split("\n"))}
              data-testid={`tier-benefits-${t.slug}`}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => deleteTier(t.id, t.name)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 px-3 py-1.5">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
              <button onClick={() => saveTier(t)} data-testid={`tier-save-${t.slug}`} className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-8 border-t border-white/5">
        <h3 className="font-heading text-xl text-white tracking-wider mb-1">Points Rules</h3>
        <p className="text-white/40 text-xs mb-4">How many points members earn per action.</p>
        <div className="grid gap-2">
          {rules.map((r) => (
            <div key={r.id} className="bg-[#0A0A0A] border border-white/5 rounded p-3 grid md:grid-cols-4 gap-3 items-center" data-testid={`rule-row-${r.event}`}>
              <div className="text-white/70 text-sm uppercase tracking-wider">{r.event.replace(/_/g, " ")}</div>
              <input type="number" className="input-dark" value={r.points}
                     onChange={(e) => setRules((p) => p.map((x) => x.id === r.id ? { ...x, points: e.target.value } : x))} />
              <input className="input-dark md:col-span-1" value={r.note || ""}
                     onChange={(e) => setRules((p) => p.map((x) => x.id === r.id ? { ...x, note: e.target.value } : x))} />
              <button onClick={() => saveRule(r)} className="text-xs bg-white/5 hover:bg-[#D4AF37] hover:text-black text-white px-3 py-2 uppercase tracking-widest">
                Save
              </button>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .input-dark { background:#000; border:1px solid rgba(255,255,255,0.1); color:#fff; padding:10px 12px; font-size:13px; border-radius:4px; outline:none; }
        .input-dark:focus { border-color:#D4AF37; }
      `}</style>
    </div>
  );
}
